import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { countriesCurrencies } from '../lib'
import Chip from '../components/common/chip'
import { FormError, Payment } from '../types'
import Dialog from '../components/common/dialog'
import { useSocket } from '../hooks/use-socket'
import NextIcon from '../assets/icons/next-icon.svg'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { getPaymentsBriefData } from '../api/app-data.api'
import MoneyGreenIcon from '../assets/icons/money-icon-green.svg'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { ReportSquareContainer } from '../components/reports-brief-management/report-square-container'
import { discardReviewedPayment, downloadPaymentVoucher, sendReviewedPayment } from '../api/payments.api'
import { numberWithCommas, normalizePaymentData, handleSyncDataToLocalDB, handleCheckIfOtpCodeIsValid } from '../utils'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'

const CustomerCompanyPaymentsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { t } = useTranslation([
    'common',
    'two-factor-auth',
    'reports-management',
    'customer-details-management',
    'payments-to-review-management',
    'customer-company-details-management',
    'document-payments-details-management'
  ])
  const { userSession } = useContext(UserContext)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { processingBatch } = useContext(AppStateContext)
  const currentPaymentsDataRef = useRef<Array<Payment>>([])
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [numPaidPayments, setNumPaidPayments] = useState<number>(0)
  const [paymentsList, setPaymentsList] = useState<Array<Payment>>([])
  const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [numPendingPayments, setNumPendingPayments] = useState<number>(0)
  const [numRejectedPayments, setNumRejectedPayments] = useState<number>(0)
  const [numReturnedPayments, setNumReturnedPayments] = useState<number>(0)
  const [numValidatedPayments, setNumValidatedPayments] = useState<number>(0)
  const [wantsToDiscardPayment, setWantsToDiscardPayment] = useState<boolean>(false)
  const [paymentDetailsIsLoading, setPaymentDetailsIsLoading] = useState<boolean>(false)
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)
  const [dialogSendPaymentConfimationIsLoading, setDialogSendPaymentConfimationIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on('addPaymentsBriefData', async (dataFromSocket: any) => {
      if (selectedCurrency === dataFromSocket.currency) {
        handleGetPaymentBriefDataFromRemote()
      }
    })

    appDataSocket.on('updatePaymentsBriefData', async (dataFromSocket: any) => {
      if (selectedCurrency === dataFromSocket.currency) {
        handleGetPaymentBriefDataFromRemote()
      }
    })

    appDataSocket.on('deletePaymentsBriefData', async (dataFromSocket: any) => {
      if (selectedCurrency === dataFromSocket.currency) {
        handleGetPaymentBriefDataFromRemote()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPaymentsBriefData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetPaymentsBriefData = async () => {
    let localPaymentsBriefDataToSet: any = {}
    const localPaymentsBriefData = globalThis.sessionStorage.getItem(`paymentsBriefData:${selectedCountry}`)

    if (localPaymentsBriefData) {
      localPaymentsBriefDataToSet = JSON.parse(localPaymentsBriefData)
      handleSetInitialData(localPaymentsBriefDataToSet)
    }

    handleGetPaymentBriefDataFromRemote()
  }

  const handleGetPaymentBriefDataFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsBriefData(selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`paymentsBriefData:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchesBriefData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    setNumPaidPayments(dataToSet.numPaidPayments)
    setNumPendingPayments(dataToSet.numPendingPayments)
    setNumRejectedPayments(dataToSet.numRejectedPayments)
    setNumReturnedPayments(dataToSet.numReturnedPayments)
    setNumValidatedPayments(dataToSet.numValidatedPayments)

    const paymentsNormalizedData = []

    for (const element of dataToSet.lastestPaymentsData) {
      const paymentData = { ...element }
      const paymentDataNormalized = normalizePaymentData(paymentData) as Payment
      paymentsNormalizedData.push(paymentDataNormalized)
    }

    paymentsNormalizedData.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      return Date.parse(String(nextPayment.createdAt)) - Date.parse(String(thisPayment.createdAt))
    })

    setPaymentsList(paymentsNormalizedData)

    if (processingBatch === false) {
      for (const element of paymentsNormalizedData) {
        const paymentToAdd = normalizePaymentData(element)
        handleSyncDataToLocalDB('payments', paymentToAdd)
      }
    }

    currentPaymentsDataRef.current = paymentsNormalizedData
    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
      setDataFullyLoaded(true)
    }

    dataFullyLoadedRef.current = true
  }

  const handleGetPaymentChipStatusText = (paymentDataStatus: string) => {
    if (paymentDataStatus === 'register-disperse-completed' || paymentDataStatus === 'processing') {
      return t('paymentStatus.processing', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'validated') {
      return t('paymentStatus.validated', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'reviewed') {
      return t('paymentStatus.pending', { ns: 'customer-details-management' })
      // return t('paymentStatus.reviewed', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'paid') {
      return t('paymentStatus.paid', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'for-review') {
      return t('paymentStatus.pending', { ns: 'customer-details-management' })
      // return t('paymentStatus.forReview', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'waiting-for-resolution') {
      return t('paymentStatus.waitingForResolution', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'rejected') {
      return t('paymentStatus.rejected', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'returned') {
      return t('paymentStatus.returned', { ns: 'customer-details-management' })
    }

    if (paymentDataStatus === 'error') {
      return t('paymentStatus.error', { ns: 'customer-details-management' })
    }

    return t('paymentStatus.processing', { ns: 'customer-details-management' })
  }

  const handleDownloadPaymentVoucher = async () => {
    try {
      setErrors({})
      setPaymentDetailsIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const paymentVoucherDownloaded = await downloadPaymentVoucher(String(selectedPaymentDetails._id), accessToken)

      window.open(paymentVoucherDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setPaymentDetailsIsLoading(false)
  }

  const handleDiscardPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setOtpCode('')
      setErrors({})

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await discardReviewedPayment(String(selectedPaymentDetails._id), accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-deleted-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSendPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setOtpCode('')
      setErrors({})

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendReviewedPayment(String(selectedPaymentDetails._id), accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-sent-successfully')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.home', { ns: 'customer-company-details-management' })}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.individualPayments', { ns: 'customer-company-details-management' })}
        </Typography>
      </Breadcrumbs>
      <div
        style={{ marginTop: '20px' }}
        className="reports-row-container"
      >
        <ReportSquareContainer
          showMainIcon
          loaderWidth={80}
          showPendingIcon
          showButtonGoDetails
          icon={MoneyGreenIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          className="reports-square-payments"
          mainData={`${numberWithCommas(numPendingPayments)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-to-review', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('individualPayments.names.pending', { ns: 'reports-management' })}
          detailsButtonText={t('individualPayments.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          loaderWidth={80}
          showValidatedIcon
          showButtonGoDetails
          icon={MoneyGreenIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          className="reports-square-payments"
          mainData={`${numberWithCommas(numValidatedPayments)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-validated', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('individualPayments.names.validated', { ns: 'reports-management' })}
          detailsButtonText={t('individualPayments.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          showCheckIcon
          loaderWidth={80}
          showButtonGoDetails
          icon={MoneyGreenIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          className="reports-square-payments"
          mainData={`${numberWithCommas(numPaidPayments)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-completed', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('individualPayments.names.paid', { ns: 'reports-management' })}
          detailsButtonText={t('individualPayments.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          showReturnedIcon
          loaderWidth={80}
          showButtonGoDetails
          icon={MoneyGreenIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          className="reports-square-payments"
          mainData={`${numberWithCommas(numReturnedPayments)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-returned', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('individualPayments.names.returned', { ns: 'reports-management' })}
          detailsButtonText={t('individualPayments.buttonActionText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          showCancelIcon
          loaderWidth={80}
          showButtonGoDetails
          icon={MoneyGreenIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          dataFullyLoaded={dataFullyLoaded}
          className="reports-square-payments"
          mainData={`${numberWithCommas(numRejectedPayments)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-rejected', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('individualPayments.names.rejected', { ns: 'reports-management' })}
          detailsButtonText={t('individualPayments.buttonActionText', { ns: 'reports-management' })}
        />
      </div>
      <div className="customer-details-actions-bar-container customer-company-payments-actions-bar-container">
        <div className="customer-details-actions-bar-title">{t('individualPayments.actionsBar.title', { ns: 'reports-management' })}</div>
        <div
          onClick={() => navigate(`/customer-company-payments-history/${userSession?.belongsTo}`, { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          className="report-bottom-data-container"
        >
          <div className="report-details-button-container">
            <div className="button-text">{t('individualPayments.seeAllPayments', { ns: 'reports-management' })}</div>
          </div>
        </div>
      </div>
      <div className="table-header-container table-header-batches">
        <div className="text-header table-header-width-20">{t('individualPayments.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-40">{t('individualPayments.tableHeaders.beneficiary', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-20 num-of-payments-batches">{t('individualPayments.tableHeaders.reference', { ns: 'reports-management' }).toUpperCase()}</div>
        <div className="text-header table-header-width-20 status-batches">{t('individualPayments.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
        </div>
      ) : (
        <div className="documents-list-container">
          {paymentsList.map((paymentData: Payment, index: number) => {
            let paymentChipStatusText = handleGetPaymentChipStatusText(paymentData.status)
            const paymentChipStatusColor = paymentData.status === 'reviewed' || paymentData.status === 'for-review' ? 'pending' : paymentData.status

            return (
              <div
                key={index}
                onClick={() => {
                  if (paymentData.status === 'for-review' || paymentData.status === 'error' || paymentData.status === 'waiting-for-resolution') {
                    navigate(`/payment-to-review-details/${String(paymentData._id)}`, { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })
                    return
                  }

                  if (selectedPaymentDetails.status === 'reviewed') {
                    setOpenSendPaymentConfimationModal(true)
                    return
                  }

                  setSelectedPaymentDetails(paymentData)
                  setOpenPaymentDetailsModal(true)
                }}
                className="documents-list-item-data-container"
              >
                <div className="batch-list-row-item row-item-width-20">{format(new Date(paymentData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                <div className="batch-list-row-item row-item-width-40">{paymentData.customerRegisteredFullName?.toUpperCase()}</div>
                <div className="batch-list-row-item row-item-width-20 num-of-payments-batches">{paymentData.referenceNumber}</div>
                <div className="batch-list-row-item row-item-width-20 status-batches">
                  <Chip
                    status={paymentChipStatusColor}
                    label={paymentChipStatusText}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
      {openPaymentDetailsModal && (
        <Dialog
          errors={errors}
          dialogIsLoading={paymentDetailsIsLoading}
          closeOptionWarning={selectedPaymentDetails.status === 'reviewed'}
          closeButtonText={
            selectedPaymentDetails.status === 'reviewed'
              ? t('paymentDetailsModal.discardPayment', { ns: 'document-payments-details-management' })
              : t('paymentDetailsModal.close', { ns: 'document-payments-details-management' })
          }
          hideActionsButtons={
            userSession?.role?.includes('-builder') ||
            (selectedPaymentDetails.status !== 'paid' && selectedPaymentDetails.status !== 'reviewed') ||
            (selectedPaymentDetails.status === 'reviewed' && selectedPaymentDetails.documentId !== '')
          }
          dialogTitle={t('paymentDetailsModal.title', { ns: 'document-payments-details-management' })}
          handleDialogAction={() => {
            if (selectedPaymentDetails.status === 'reviewed') {
              setOpenSendPaymentConfimationModal(true)
            } else if (selectedPaymentDetails.status === 'paid') {
              handleDownloadPaymentVoucher()
            }
          }}
          handleCloseOptionAction={() => {
            setErrors({})

            if (selectedPaymentDetails.status === 'reviewed') {
              setWantsToDiscardPayment(true)
              setOpenSendPaymentConfimationModal(true)
              return
            }

            setOpenPaymentDetailsModal(false)
            setSelectedPaymentDetails({})
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setWantsToDiscardPayment(false)
            }

            setErrors({})
            setOpenPaymentDetailsModal(openState)
            setSelectedPaymentDetails({})
          }}
          actionButtonText={
            selectedPaymentDetails.status === 'paid'
              ? t('paymentDetailsModal.actionButtonText', { ns: 'document-payments-details-management' })
              : t('paymentDetailsModal.sendActionButtonText', { ns: 'document-payments-details-management' })
          }
        >
          <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
        </Dialog>
      )}
      {openSendPaymentConfimationModal && (
        <Dialog
          errors={errors}
          handleDialogAction={() => {
            if (wantsToDiscardPayment) {
              handleDiscardPayment()
              return
            }

            handleSendPayment()
          }}
          actionButtonWarningStyle={wantsToDiscardPayment}
          actionButtonSuccessStyle={!wantsToDiscardPayment}
          dialogIsLoading={dialogSendPaymentConfimationIsLoading}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          actionButtonText={
            wantsToDiscardPayment ? t('confirmSendPaymentModal.rejectActionButtonText', { ns: 'payments-to-review-management' }) : t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })
          }
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setWantsToDiscardPayment(false)
            }

            setOpenSendPaymentConfimationModal(openState)
          }}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default CustomerCompanyPaymentsManagement
